// Account/utility-page left side nav, rendered in-tree by the shared account
// document templates (#33285). The page serializes the link / link_list
// StructBlocks into plain SideNavItem props (URLs + active-state resolved
// server-side); this component only renders that resolved structure. `html`
// blocks are not serialized and not rendered here.
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

type ISideNavItem = templates.ProfileDocumentTemplate["side_nav"][number];
type ISideNavLink = NonNullable<ISideNavItem["link"]>;

const Link = ({
    link,
}: {
    link: Immutable<ISideNavLink>;
}): React.JSX.Element => (
    <div className="accordion__list-item">
        <a
            href={link.url}
            className={link.is_active ? "accordion--is-active" : undefined}
        >
            {link.display_name}
        </a>
    </div>
);

// `active` opens the accordion group on initial render.
//
// `selfManaged` (default false) chooses who drives the open/close toggle:
//   - false: static markup only -- the host site's global `.accordion` click
//     handler (its ui/helpers.ts) toggles the `--is-active` classes. Byte-exact
//     to the legacy render, used by sites that still ship that handler.
//   - true: this component owns the toggle via its own state + onClick (no
//     global handler needed), for sites whose ui/helpers.ts has been retired.
// Either way the initial render is identical (state is seeded from `active`), so
// SSR markup matches hydration.
const LinkList = ({
    item,
    active = false,
    selfManaged = false,
}: {
    item: Immutable<NonNullable<ISideNavItem["link_list"]>>;
    active?: boolean;
    selfManaged?: boolean;
}): React.JSX.Element => {
    const [isOpen, setIsOpen] = React.useState(active);
    return (
        <div className="accordion__list-item">
            <a
                className={
                    isOpen ? "accordion accordion--is-active" : "accordion"
                }
                role="button"
                tabIndex={0}
                aria-expanded={
                    selfManaged ? (isOpen ? "true" : "false") : undefined
                }
                onClick={
                    selfManaged
                        ? () => {
                              setIsOpen((open) => !open);
                          }
                        : undefined
                }
                onKeyDown={
                    selfManaged
                        ? (event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  setIsOpen((open) => !open);
                              }
                          }
                        : undefined
                }
            >
                {item.list_header} <span />
            </a>
            <div
                className={
                    isOpen
                        ? "accordion-target accordion-target--is-active"
                        : "accordion-target"
                }
            >
                <ul className="accordion-target__list">
                    {item.links.map((link, i) => (
                        <li className="accordion-target__list-item" key={i}>
                            <a
                                href={link.url}
                                className={
                                    link.is_active ? "is-active" : undefined
                                }
                            >
                                {link.display_name}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

const NavItems = ({
    items,
    expandFirstLinkList = false,
    selfManaged = false,
}: {
    items: Immutable<readonly ISideNavItem[]>;
    // Mark the first `link_list` group active (legacy helpers.ts only expands
    // the first desktop accordion, so this is passed to the desktop list only).
    // It is the first `link_list`, not the first item: a plain `link` never
    // carries the base `accordion` class, so the legacy `.accordion` selector
    // skips it.
    expandFirstLinkList?: boolean;
    selfManaged?: boolean;
}): React.JSX.Element => {
    let firstLinkListSeen = false;
    return (
        <>
            {items.map((item, i) => {
                if (item.block_type === "link" && item.link) {
                    return <Link key={i} link={item.link} />;
                }
                if (item.block_type === "link_list" && item.link_list) {
                    const active = expandFirstLinkList && !firstLinkListSeen;
                    firstLinkListSeen = true;
                    return (
                        <LinkList
                            key={i}
                            item={item.link_list}
                            active={active}
                            selfManaged={selfManaged}
                        />
                    );
                }
                return null;
            })}
        </>
    );
};

// Mobile MENU toggle: reveals the mobile nav's `.accordion-target`. Static
// (relies on the host's global handler) unless `selfManaged`, in which case it
// owns its collapsed-by-default toggle.
const MobileNav = ({
    items,
    selfManaged,
}: {
    items: Immutable<readonly ISideNavItem[]>;
    selfManaged: boolean;
}): React.JSX.Element => {
    const [isOpen, setIsOpen] = React.useState(false);
    return (
        <div className="accordion__list-item accordion__list-item--mobile">
            <button
                type="button"
                className={
                    selfManaged && isOpen
                        ? "category-list-side-nav__mobile-toggle accordion accordion--is-active"
                        : "category-list-side-nav__mobile-toggle accordion"
                }
                aria-expanded={
                    selfManaged ? (isOpen ? "true" : "false") : undefined
                }
                onClick={
                    selfManaged
                        ? () => {
                              setIsOpen((open) => !open);
                          }
                        : undefined
                }
            >
                {t`menu`}
            </button>
            <div
                className={
                    selfManaged && isOpen
                        ? "accordion-target accordion-target--is-active"
                        : "accordion-target"
                }
            >
                <NavItems items={items} selfManaged={selfManaged} />
            </div>
        </div>
    );
};

export const SideNav = ({
    items,
    expandFirstLinkList = true,
    selfManaged = false,
}: {
    items: Immutable<readonly ISideNavItem[]>;
    // Whether the first desktop link_list group SSRs expanded. Defaults to
    // true for sites whose legacy on-load JS auto-expands the first desktop
    // accordion (pillows ui/helpers.ts); sites whose accordion JS only
    // toggles on click (sealy) pass false so the SSR matches their legacy
    // all-collapsed initial render.
    expandFirstLinkList?: boolean;
    // Whether this component owns the accordion open/close toggle (true) or
    // defers to the host site's global `.accordion` click handler (false,
    // default). Sites that have retired their ui/helpers.ts accordion handler
    // (toppers) pass true.
    selfManaged?: boolean;
}): React.JSX.Element => (
    <>
        <div className="accordion__list-item accordion__list-item--desktop">
            <NavItems
                items={items}
                expandFirstLinkList={expandFirstLinkList}
                selfManaged={selfManaged}
            />
        </div>
        <MobileNav items={items} selfManaged={selfManaged} />
    </>
);
